// Sugalarm — persistent client-side queue of pending treatment writes
// (cycle 101). Survives SPA reload and kiosk reboot via localStorage, so
// an insulin dose spoken while the Fly server is unreachable syncs when
// connectivity returns.
//
// The flush + reconcile orchestration lives in `useVoiceDispatch`; this
// module is the pure storage layer + the dedupe matcher. The server has
// no idempotency key today, so dedupe is client-side: each pending write
// carries a `clientId`, and `treatmentMatchesPending` recognizes a server
// row that actually landed before we went offline (a lost POST response)
// so a re-flush doesn't duplicate it.

import type { Treatment, TreatmentCreateIn } from "./types";

const STORAGE_KEY = "sugalarm.treatmentQueue";

export interface PendingTreatment {
  /** Client-generated id — also the id of the optimistic in-memory row,
   *  so reconciliation can drop exactly that row on success. */
  clientId: string;
  payload: TreatmentCreateIn;
  createdAt: number;
}

export function loadQueue(): PendingTreatment[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: keep only well-formed entries.
    return parsed.filter(
      (p): p is PendingTreatment =>
        p && typeof p.clientId === "string" && p.payload != null,
    );
  } catch {
    return [];
  }
}

function saveQueue(list: PendingTreatment[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* storage full / unavailable — non-fatal */
  }
}

export function enqueue(pending: PendingTreatment): PendingTreatment[] {
  const next = [...loadQueue(), pending];
  saveQueue(next);
  return next;
}

export function removePending(clientId: string): PendingTreatment[] {
  const next = loadQueue().filter((p) => p.clientId !== clientId);
  saveQueue(next);
  return next;
}

export function queueCount(): number {
  return loadQueue().length;
}

/** True when a real (server) treatment row is the landed version of a
 *  pending write — same insulin amount + within a short time window — so a
 *  re-flush after a lost POST response doesn't create a duplicate. The
 *  caller must exclude the optimistic row itself (its id === clientId). */
export function treatmentMatchesPending(
  t: Treatment,
  pending: PendingTreatment,
  toleranceMs = 15_000,
): boolean {
  if (t.id === pending.clientId) return false; // that's our optimistic row
  if (t.event_type !== "insulin") return false;
  const wantUnits = pending.payload.insulin_u;
  if (wantUnits == null || t.insulin_u == null) return false;
  if (Math.abs(Number(t.insulin_u) - Number(wantUnits)) > 1e-6) return false;
  const wantTs = pending.payload.timestamp
    ? new Date(pending.payload.timestamp).getTime()
    : pending.createdAt;
  return Math.abs(new Date(t.timestamp).getTime() - wantTs) <= toleranceMs;
}
